import React, { useEffect, useMemo, useState } from 'react';
import { FormControl } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  Checkbox,
  ContentTemplate, CustomAutocomplete,
  renderDropDownOption,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Progress, renderDropDownTags,
  Typography,
  WordCountArea,
} from 'web-library';
import { ConfigurationType } from 'web-library/models/DropDown';
import { joiResolver } from '@hookform/resolvers/joi';
import { AxiosError } from 'axios';
import { QueryObserverResult, RefetchOptions } from 'react-query';
import {
  SendUniqueLinkEmailFormData,
  usePostSendUniqueLinkEmail,
} from '../../../api-refactored/unique-link/usePostSendUniqueLinkEmail';
import { AdminDeletedSubjectDetails, SelectedUserInfo } from './model';
import { configurationTypeData } from '../../../modules/admin/logic/dropDownOption';
import AdminSelectedSubjects from './AdminSelectedSubjects';
import AdminSubjectListing from './AdminSubjectListing';
import { useGetExemptionRulesDropdown } from '../../../api-refactored/unique-link/useGetExemptionRulesDropdown';
import { AdminSelectedExamSubjects, ExamSubjects } from '../../../api/candidate/exam/model';
import createUniqueLinkFormSchema from './logic/createUniqueLinkFormSchema';
import { GenericErrorResponse } from '../../../api';
import { CoordinatorUniqueLinkCountResponse } from '../../../api-refactored/unique-link/useGetCoordinatorUniqueLinkCount';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";
import ControlledFormControlLabel
  from "web-library/stories/atom/FormControlLabel/ControlledFormControlLabel";
import {DropDownItem} from "../../../hooks/dropdown";

const useStyles = makeStyles()((theme) =>
({
  viewDetailsDialog: {
    '& .MuiPaper-root.MuiDialog-paper': {
      padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
      '& .MuiDialogTitle-root': {
        padding: `0 0 ${theme.spacing(2)} 0`,
      },
      '& .MuiDialogContent-root': {
        padding: `0 0 ${theme.spacing(3)} 0`,
      },
      '& .MuiDialogActions-root': {
        padding: 0,
      },
    },
  },
  input: {
    marginTop: theme.spacing(3),
  },
  dialogContentContainer: {
    marginLeft: theme.spacing(0.3),
  },
  selectContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    maxWidth: '960px',
  },
  rootContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(6),
  },
  arrowIconContainer: {
    right: '-10px',
  },
  chipsContainer: {
    overflowX: 'hidden',
    width: '100%',
  },
  iconButton: {
    position: 'absolute',
    right: '45px',
    top: 'calc(50% - 12px)',
    padding: 0,
  },
  chipLabel: {
    padding: 0,
  },
  divider: {
    paddingBottom: theme.spacing(4),
  },
  errorText: {
    textAlign: 'end',
    color: theme.color.state.stateError,
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

interface GrantAccessModalProps {
  accountType: string;
  examLevelUuid: string;
  userInfo: SelectedUserInfo;
  modalState: boolean;
  onDialogClose: () => void;
  updateEmailStatus: (index: number, status: string, customMessage?: string) => void;
  showWavieSubjectFee?: boolean;
  applicationType: string;
  isGCESelected: boolean;
  refetchCoordinatorListing?: (
    options?: RefetchOptions | undefined
  ) => Promise<
    QueryObserverResult<CoordinatorUniqueLinkCountResponse[], AxiosError<GenericErrorResponse>>
  >;
  fetchCandidate?: () => void;
}

const GrantAccessModal = ({
  accountType,
  examLevelUuid,
  userInfo,
  modalState,
  onDialogClose,
  updateEmailStatus,
  showWavieSubjectFee = true,
  applicationType,
  isGCESelected,
  refetchCoordinatorListing,
  fetchCandidate,
}: GrantAccessModalProps) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const formDisabledFields = useMemo(
    () => ({ description: !(applicationType === 'EXAM') }),
    [accountType, applicationType]
  );
const formHook = useForm<SendUniqueLinkEmailFormData>({
  mode: 'onSubmit',
  resolver: joiResolver(createUniqueLinkFormSchema(formDisabledFields)),
});
  const { handleSubmit, register, control, setValue, formState: {errors} } = formHook;


  const { mutate: sendUniqueLinkEmailMutate, status: sendEmailStatus } = usePostSendUniqueLinkEmail(
    {
      onSuccess: () => {
        if (accountType === 'COORDINATORS' && refetchCoordinatorListing) {
          refetchCoordinatorListing().then();
        }

        if (accountType === 'CANDIDATES' && fetchCandidate) {
          fetchCandidate();
        }

        updateEmailStatus(
          userInfo.index,
          'success',
          t('admin->>UniqueLink.EmailHasSent', { name: userInfo.name })
        );
        setTimeout(() => {
          updateEmailStatus(userInfo.index, '');
        }, 5000);

        onDialogClose();
      },
      onError: (error) => {
        updateEmailStatus(
          userInfo.index,
          'error',
          error.response ? error.response.data.message : error.message
        );
        onDialogClose();
      },
    }
  );

  const [exemptionRulesValues, setExemptionRulesValues] = useState<DropDownItem[]>([]);

  const [dateOfExpiry, setDateOfExpiry] = useState<null | string>(null);

  const [isOpenExemptionRulesDropdown, setIsOpenExemptionRulesDropdown] = useState<boolean>(false);

  const [adminSelectedSubjects, setAdminSelectedSubjects] = useState<AdminSelectedExamSubjects[]>(
    []
  );

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [adminEditedSubjectDetails, setAdminEditedSubjectDetails] = useState<ExamSubjects>({
    subjectUuid: '',
    subjectName: '',
    subjectCode: '',
    examSeries: '',
    examLevel: '',
    subjectLevel: '',
    language: '',
    actionRequired: false,
    allowedToAdd: false,
  });

  const [adminDeletedSubjectDetails, setAdminDeletedSubjectDetails] =
    useState<AdminDeletedSubjectDetails>({
      subjectName: '',
      subjectCode: '',
    });

  const { data: exemptionRules, isLoading: isLoadingExemptionRules } =
    useGetExemptionRulesDropdown();

  const ET12Object = useMemo(
    () =>
      exemptionRules?.find(
        (exemptionRule) => exemptionRule.description === 'Entry Requirement (ET12)'
      ),
    [exemptionRules]
  );

  const ET1Object = useMemo(
    () =>
      exemptionRules?.find(
        (exemptionRule) => exemptionRule.description === 'Candidate Type Requirement (ET1)'
      ),
    [exemptionRules]
  );

  const isET1Selected = useMemo(
    () =>
      exemptionRulesValues.some((exemptionRulesValue) => exemptionRulesValue.value === ET1Object?.code),
    [ET1Object?.code, exemptionRulesValues]
  );

  const isET12Selected = useMemo(
    () =>
      exemptionRulesValues.some((exemptionRulesValue) => exemptionRulesValue.value === ET12Object?.code),
    [ET12Object?.code, exemptionRulesValues]
  );

  const handleSendUniqueLinkEmail = (formData: SendUniqueLinkEmailFormData) => {
    if (isET12Selected && adminSelectedSubjects.length === 0) {
      setErrorMessage(t('admin->>UniqueLink.NoSubjectSelectedErrorMessage'));
    } else {
      const subjectPayloadForSendUniqueLink = adminSelectedSubjects.map(
        (adminSelectedSubject: AdminSelectedExamSubjects) => ({
          subjectCode: adminSelectedSubject.subjectCode,
          examSeriesUuid: adminSelectedSubject?.examSeriesUuid || null,
          optionalPaperUuid:
            adminSelectedSubject?.optionalPapers
              ?.map((optionalPaper: { paperNo: string; selected: boolean; uuid: string }) =>
                optionalPaper.selected ? optionalPaper.uuid : null
              )
              .filter((optionalPaperUuid) => optionalPaperUuid) || [],
        })
      );
      sendUniqueLinkEmailMutate({
        isWaiveSubjectAdminFee: formData.isWaiveSubjectAdminFee,
        isWaiveLateFee: formData.isWaiveLateFee,
        isOfflinePayment: accountType === 'CANDIDATES' ? formData.isOfflinePayment : null,
        userType: accountType,
        uuid: userInfo.uuid,
        examLevelUuid,
        applicationType,
        exemptionRuleUuids: exemptionRulesValues.map((v)=>v.value),
        subjects: subjectPayloadForSendUniqueLink,
        expiryDate: formData.expiryDate,
        description: formData.description,
      });
    }
  };

  useEffect(() => {
    if ((isET12Selected && adminSelectedSubjects.length) || !isET12Selected) {
      setErrorMessage('');
    }
  }, [adminSelectedSubjects.length, isET12Selected]);

  useEffect(() => {
    if (!(isET1Selected || isET12Selected)) {
      setAdminSelectedSubjects([]);
    }
  }, [isET12Selected, isET1Selected]);

  if (isLoadingExemptionRules) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <Dialog
      id="unique-link-grant-access"
      open={modalState}
      maxWidth="lg"
      onClose={onDialogClose}
      className={classes.viewDetailsDialog}
    >
      <DialogTitle>{t('admin->>UniqueLink.GrantAccess')}</DialogTitle>

      <form onSubmit={handleSubmit(handleSendUniqueLinkEmail)}>
        <DialogContent>
          <Box className={classes.dialogContentContainer}>
            <Typography>{t('admin->>UniqueLink.GrantAccessSelectType')}</Typography>

            <FormControl>
              {showWavieSubjectFee && (
                  <ControlledFormControlLabel
                      render={({onChange, value}) => <Checkbox onChange={e => onChange(e.target.checked)}
                                                               checked={value} />}
                      defaultValue={false}
                      name="isWaiveSubjectAdminFee"
                      control={control}
                      label={t('admin->>UniqueLink.WaiveSubjectAdminFees')}
                  />
              )}

              <ControlledFormControlLabel
                  render={({onChange, value}) => <Checkbox onChange={e => onChange(e.target.checked)}
                                                           checked={value} />}
                  defaultValue={false}
                  name="isWaiveLateFee"
                  control={control}
                  label={t('admin->>UniqueLink.WaiveLateFees')}
              />

              {accountType === 'CANDIDATES' && (
                  <ControlledFormControlLabel
                      render={({onChange, value}) => <Checkbox onChange={e => onChange(e.target.checked)}
                                                               checked={value} />}
                      defaultValue={false}
                      name="isOfflinePayment"
                      control={control}
                      label={t('admin->>UniqueLink.OfflinePayment')}
                  />
              )}
            </FormControl>
            {isGCESelected && (
              <Box className={classes.selectContainer}>
                <CustomAutocomplete<DropDownItem, true,false, undefined>
                  id="inputSelect-rulesValidationsToBypass"
                  label={t('admin->>UniqueLink.RulesValidationsToBypass')}
                  value={exemptionRulesValues}
                  multiple
                  disableCloseOnSelect
                  options={configurationTypeData(exemptionRules as ConfigurationType[])}
                  onChange={(_event, value) =>
                  {
                    const valueArr = value.map((individualValue) => individualValue.value);
                    setExemptionRulesValues(value);
                  }
                  }
                  isOptionEqualToValue={(option, v) => option.key === v.key}
                  renderOption={renderDropDownOption}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.children)}
                  renderTags={renderDropDownTags}
                />
              </Box>
            )}
            {applicationType === 'EXAM' && (
              <Box className={classes.selectContainer}>
                <ControlledDatePicker
                  label={t('admin->>UniqueLink.ExpiryDateLabel')}
                  value={dateOfExpiry}
                  id="input-text-expiryDate"
                  // @ts-ignore
                  onChange={(date) => {
                    setDateOfExpiry(date || '');
                    setValue('expiryDate', date || '');
                  }}
                  control={control}
                  name="expiryDate"
                  disablePast
                  isRequired
                  error={!!errors.expiryDate}
                  helperText={errors?.expiryDate?.message?.toString()}
                  inputVariant="filled"
                />
                <WordCountArea
                    className={classes.input}
                    label={t('admin->>UniqueLink.DescriptionLabel')}
                    placeholder={t('admin->>UniqueLink.DescriptionPlaceholder')}
                    maxLength={200}
                    isRequired
                    minRows={3}
                    error={!!errors?.description}
                    form={formHook}
                    name="description"
                    // CPEPOKPNEH-3391
                    onChange={(e) => {
                        const trimmedValue = e.target.value.slice(0, 200); // enforce maxLength
                        formHook.setValue('description', trimmedValue);
                    }}
                />
              </Box>
            )}
            {(isET1Selected || isET12Selected) && !isOpenExemptionRulesDropdown && (
              <>
                <AdminSubjectListing
                  adminSelectedSubjects={adminSelectedSubjects}
                  setAdminSelectedSubjects={setAdminSelectedSubjects}
                  adminEditedSubjectDetails={adminEditedSubjectDetails}
                  setAdminEditedSubjectDetails={setAdminEditedSubjectDetails}
                />
                <Divider className={classes.divider} />
                <AdminSelectedSubjects
                  adminSelectedSubjects={adminSelectedSubjects}
                  setAdminSelectedSubjects={setAdminSelectedSubjects}
                  adminEditedSubjectDetails={adminEditedSubjectDetails}
                  setAdminEditedSubjectDetails={setAdminEditedSubjectDetails}
                  adminDeletedSubjectDetails={adminDeletedSubjectDetails}
                  setAdminDeletedSubjectDetails={setAdminDeletedSubjectDetails}
                />
              </>
            )}
          </Box>
        </DialogContent>
        {errorMessage && (
          <Typography className={classes.errorText} variant="body2">
            {errorMessage}
          </Typography>
        )}
        <DialogActions>
          <Button type="button" variant="text" onClick={onDialogClose}>
            {t('admin->>UniqueLink.Cancel')}
          </Button>

          <ApiButton type="submit" status={sendEmailStatus}>
            {t('admin->>UniqueLink.SendEmail')}
          </ApiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
export default GrantAccessModal;
